/**
 * The settlement, behind every public page, surviving the route change.
 *
 * Janeks: *"upon pressing play now, the view from the hero is panned to the
 * right side where we see the login form. So that we get not the preview image
 * on the left side, but the very same 3D environment with the campfire scene.
 * So act as if camera pans from website, to login. For this, keep the 3d scene
 * parallaxed all the time in the background."*
 *
 * ─── Why this is a singleton outside `#app` ─────────────────────────────────
 * `main.ts` renders each route by replacing the contents of `#app`. A scene
 * living inside the landing page's markup — which is where it was — is
 * destroyed the instant the router navigates to `/login`, which makes a camera
 * move ACROSS that navigation impossible: there is nothing left to move.
 *
 * So the canvas is mounted once into `document.body`, behind `#app`, and is
 * owned by this module rather than by any page. Routes acquire and release it
 * (`acquireBackdrop` / `releaseBackdrop`); it is built on the first acquire and
 * torn down when the last holder lets go, which is the navigation to `/play`
 * or to anything else that is not a public page.
 *
 * ─── The ROUTER owns it, not the pages ─────────────────────────────────────
 * `syncBackdrop(path)` is called from `main.ts` at the top of `render()`,
 * BEFORE the outgoing page is torn down, and it decides from the path alone
 * whether a scene should be up and which way it should be framed.
 *
 * It was reference-counted by the pages first, on the assumption that the
 * outgoing and incoming pages briefly overlap. **They do not.** `render()`
 * calls `teardown?.()` as its first statement and mounts the next route
 * afterwards, so the count reached zero on every single navigation: the scene
 * was disposed and rebuilt each time, the settlement reset to its opening
 * state instead of panning, and on the way back the landing page rendered its
 * own opaque gradients for the second or two the rebuild took — which looked
 * exactly like the backdrop had never been added.
 *
 * A page cannot own something that must outlive it. The router can.
 *
 * ─── It is optional, and silent when it is absent ──────────────────────────
 * No WebGL, a narrow viewport, or a chunk that failed to load leaves
 * `current` null and every method a no-op. The pages behind it keep their
 * poster and their gradients, which is what they had before this existed.
 */
import type { LandingScene } from './landing-scene';
import { isLegalPath } from './legal-paths';

/** Marks the document while a live scene is up, so CSS can drop the posters. */
const LIVE_CLASS = 'lp-backdrop-live';
const LAYER_ID = 'lp-backdrop';
const STYLE_ID = 'lp-backdrop-styles';

/**
 * The layer's own stylesheet, injected by this module rather than living in
 * `landing-styles.ts`.
 *
 * It has to be here because the layer outlives the landing page and appears on
 * routes that never inject that sheet. It did live there, and the auth screens
 * consequently mounted the layer with no CSS at all: a plain `div` in normal
 * flow with zero height, so `startLandingScene` sized its renderer to the
 * host's `clientHeight` and got a **1600x1** canvas. Nothing errors — a
 * one-pixel scene renders perfectly well.
 *
 * Whoever owns an element owns the rules that give it a box.
 */
const LAYER_CSS = `
#${LAYER_ID} {
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background: #0b0805;
}
#${LAYER_ID} canvas { display: block; width: 100%; height: 100%; }
/* Whatever the router rendered sits above the scene and must stop painting
   over it. NOT the body: the body keeps its own dark fill, or the browser's
   white base shows through wherever the canvas does not reach — which is
   exactly what a blown-out white login screen turned out to be. */
body.${LIVE_CLASS} #app { position: relative; z-index: 1; background: transparent; }
`;

function ensureStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = LAYER_CSS;
  document.head.appendChild(style);
}

/**
 * Below this width the backdrop is not run at all.
 *
 * The same threshold and the same reasoning as the hero's: the map is trimmed
 * for a wide frame, and a narrow portrait window sees a tall slice of mostly
 * ground and sky for the full cost of every GLB in it.
 */
export const BACKDROP_MIN_WIDTH = 900;

let layer: HTMLElement | null = null;
let current: LandingScene | null = null;
/** Set while the dynamic import is in flight, so a release can cancel it. */
let loading = false;
let scrollBound = false;

/** Is a live scene up? False on a phone, under reduced motion, or without WebGL. */
export function backdropIsLive(): boolean {
  return current !== null;
}

export function backdropSupported(): boolean {
  return window.innerWidth >= BACKDROP_MIN_WIDTH
    && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Public routes that show the settlement behind them.
 *
 * The auth screens are listed by path rather than inferred, because getting
 * this wrong in the permissive direction is worse than getting it wrong in the
 * strict one: a scene left running under `/play` is a second WebGL context and
 * a second copy of the settlement competing with the game for the GPU.
 */
const AUTH_PATHS = new Set([
  '/login', '/register', '/forgot-password', '/reset-password',
  '/verify-email', '/check-email',
]);

/** Landing, news and the policy pages all frame the scene the same way. */
function isPageRoute(path: string): boolean {
  return path === '/' || path === '/news'
    || path.startsWith('/news/') || isLegalPath(path);
}

/**
 * Brings the backdrop up, takes it down, or re-frames it — from the path
 * alone.
 *
 * Call it BEFORE tearing the outgoing page down. Calling it for a route that
 * already matches is free: an existing scene is kept and only its framing is
 * set, which is what makes the camera travel across a navigation rather than
 * being rebuilt on the far side of it.
 */
export function syncBackdrop(path: string): void {
  const auth = AUTH_PATHS.has(path);
  const wanted = auth || isPageRoute(path);
  if (!wanted) {
    teardownBackdrop();
    return;
  }
  ensureUp();
  // Set on every sync, including while the scene is still loading: the scene
  // reads its framing from module state when it starts, so a cold link
  // straight to `/login` opens already framed rather than panning across on
  // arrival.
  pendingFraming = auth ? 1 : 0;
  current?.setFraming(pendingFraming);
}

/** What the framing should be, held for a scene that has not loaded yet. */
let pendingFraming = 0;

function ensureUp(): void {
  if (current || loading || !backdropSupported()) return;

  ensureStyles();
  layer = document.createElement('div');
  layer.id = LAYER_ID;
  // Behind `#app`, which is why `#app` needs a transparent background for any
  // of this to be visible — see this module's own stylesheet.
  document.body.prepend(layer);

  loading = true;
  void import('./landing-scene')
    .then(({ startLandingScene }) => {
      loading = false;
      // Torn down while the chunk was in flight: the layer is already gone and
      // starting a scene into it would leak a WebGL context nobody holds.
      if (!layer) return;
      current = startLandingScene(layer, { reducedMotion: false });
      current.setFraming(pendingFraming);
      document.body.classList.add(LIVE_CLASS);
      bindScroll();
      current.setScroll(window.scrollY);
    })
    .catch((err) => {
      loading = false;
      console.warn('[landing] backdrop unavailable, keeping the static art', err);
      layer?.remove();
      layer = null;
    });
}

function teardownBackdrop(): void {
  unbindScroll();
  current?.dispose();
  current = null;
  layer?.remove();
  layer = null;
  document.body.classList.remove(LIVE_CLASS);
}

/**
 * Pans between the landing framing (0) and the auth framing (1).
 *
 * The scene eases toward it over its own frames, so this is a destination
 * rather than a step — set it once per navigation and let the pan run.
 */
export function setBackdropFraming(target: number): void {
  current?.setFraming(target);
}

export function backdropFraming(): number {
  return current?.framing() ?? 0;
}

// ─── scroll parallax ─────────────────────────────────────────────────────────

/**
 * Throttled to one update per frame.
 *
 * A scroll listener fires far more often than the display refreshes, and every
 * call here does trigonometry and moves a camera. Coalescing to the frame is
 * the difference between a parallax and a jank.
 */
let scrollRaf = 0;
function onScroll(): void {
  if (scrollRaf) return;
  scrollRaf = requestAnimationFrame(() => {
    scrollRaf = 0;
    current?.setScroll(window.scrollY);
  });
}

function bindScroll(): void {
  if (scrollBound) return;
  window.addEventListener('scroll', onScroll, { passive: true });
  scrollBound = true;
}

function unbindScroll(): void {
  if (!scrollBound) return;
  window.removeEventListener('scroll', onScroll);
  if (scrollRaf) cancelAnimationFrame(scrollRaf);
  scrollRaf = 0;
  scrollBound = false;
}

/** Test seam. */
export function resetBackdrop(): void {
  teardownBackdrop();
}
